import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const contactMethods = [
    {
      title: 'Direct Advisory Line',
      description: 'Speak directly with our transaction coordinators.',
      contact: '+256 774 509 859',
      meta: 'Mon-Fri: 8AM - 6PM EAT'
    },
    {
      title: 'Digital Communications',
      description: 'Send us a detailed transaction or support request.',
      contact: 'info@hirebroker.co',
      meta: 'Monitored 24/7 • Response within 24hrs'
    },
    {
      title: 'Central Hub',
      description: 'Meeting physical advisors in Kampala, Uganda.',
      contact: 'Plot 22B, Kampala Road, Nakasero',
      meta: 'Strictly by appointment only'
    }
  ];

  const faqs = [
    {
      q: 'Who holds my rental hunting fee, and when is the realtor paid?',
      a: 'You pay the full brokerage fee to HireBroker in the app. HireBroker holds it, covers the realtor’s transport from that fee, and pays the realtor the remaining amount only after you accept a property. If you have an issue with the realtor or do not want the properties shown, HireBroker verifies the issue and either assigns another realtor or refunds the remaining balance. HireBroker does not hold money for land transactions. See /how-fees-work.'
    },
    {
      q: 'What is the absolute turnaround time for property verification?',
      a: 'Every property uploaded under our flat 10,000 UGX structure undergoes physical metadata and baseline credential authentication within 24 hours before being pushed to our active broker network.'
    },
    {
      q: 'Can I list land or seek rental assistance if I am in the diaspora?',
      a: 'Yes. Our platform was architected specifically to eliminate proxy fraud. We offer completely remote land title checks and detailed compliance logging for international property seekers.'
    },
    {
      q: 'How do you pick the brokers assigned to my request?',
      a: 'We entirely bypass casual street agents. Requests are dynamically routed into our system based strictly on localized zone metrics, past feedback performance ratings, and verified zoning expertise.'
    }
  ];

  return (
    <div className="min-h-screen bg-white text-neutral-900 selection:bg-red-50 selection:text-red-900">
      <Header />
      
      <main className="pt-24">
        
        {/* SECTION 1: HERO (Pristine White Background) */}
        <section className="bg-white tracking-tight">
          <div className="max-w-6xl mx-auto px-6 pt-16 pb-20">
            <div className="mb-8 opacity-70">
              <Breadcrumbs />
            </div>
            
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                Connect With Us
              </span>
              <h1 className="text-4xl sm:text-6xl font-light text-neutral-950 leading-[1.1] mb-6">
                Let’s secure your next <span className="font-serif italic text-neutral-500 block sm:inline">property transaction.</span>
              </h1>
              <p className="text-lg text-neutral-500 font-light leading-relaxed max-w-xl">
                Questions about rental hunting fees, rematches, refunds, listings, or broker verification? Send a message or read how hunting fees work.
              </p>
            </div>
          </div>
        </section>


        {/* SECTION 2: INTERACTIVE INTERFACE & CHANNELS (Soft Chalk Grey Background) */}
        <section className="bg-neutral-50 border-y border-neutral-200/60 py-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-12 gap-16 items-start">
              
              {/* Left Column: Premium Form Architecture */}
              <div className="lg:col-span-7 bg-white border border-neutral-200/50 p-8 sm:p-10 shadow-sm">
                <div className="mb-8">
                  <h2 className="text-2xl font-light text-neutral-950 tracking-tight">Send a Message</h2>
                  <p className="text-xs text-neutral-400 mt-1">Fields marked with an asterisk (*) are strictly mandatory.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-xs uppercase tracking-wider text-neutral-500 font-medium mb-2">
                        Full Name <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-white px-4 py-3 border border-neutral-200 text-sm text-neutral-900 rounded-none focus:outline-none focus:border-neutral-950 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs uppercase tracking-wider text-neutral-500 font-medium mb-2">
                        Email Address <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-white px-4 py-3 border border-neutral-200 text-sm text-neutral-900 rounded-none focus:outline-none focus:border-neutral-950 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-xs uppercase tracking-wider text-neutral-500 font-medium mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+256..."
                        className="w-full bg-white px-4 py-3 border border-neutral-200 text-sm text-neutral-900 rounded-none focus:outline-none focus:border-neutral-950 transition-colors placeholder:text-neutral-300"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-xs uppercase tracking-wider text-neutral-500 font-medium mb-2">
                        Inquiry Scope <span className="text-red-600">*</span>
                      </label>
                      <div className="relative">
                        <select
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                          className="w-full bg-white px-4 py-3 border border-neutral-200 text-sm text-neutral-900 rounded-none appearance-none focus:outline-none focus:border-neutral-950 transition-colors"
                        >
                          <option value="">Select infrastructure focus</option>
                          <option value="rental">Rental Hunting & Escrow Protection</option>
                          <option value="buy-land">Buying Land Safely</option>
                          <option value="sell-land">Selling Land / Listing Assets</option>
                          <option value="partnership">Broker Network Verification</option>
                          <option value="other">General Administration</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-neutral-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 9l-7 7-7-7" /></svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs uppercase tracking-wider text-neutral-500 font-medium mb-2">
                      Detailed Message Parameter <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full bg-white px-4 py-3 border border-neutral-200 text-sm text-neutral-900 rounded-none focus:outline-none focus:border-neutral-950 transition-colors resize-none"
                      placeholder="Specify neighborhood details, asset values, or support inquiries clearly..."
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="w-full py-3.5 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-colors duration-200"
                  >
                    Transmit Message
                  </button>
                </form>
              </div>

              {/* Right Column: High-End Contact Directory Layout */}
              <div className="lg:col-span-5 space-y-12 lg:pt-6">
                <div>
                  <h2 className="text-2xl font-light text-neutral-950 tracking-tight mb-2">Corporate Directory</h2>
                  <p className="text-sm text-neutral-500 font-light">Direct linkages to specialized operations managers.</p>
                </div>

                <div className="space-y-8">
                  {contactMethods.map((method, index) => (
                    <div key={index} className="space-y-1.5 border-l-2 border-neutral-200 pl-4 focus-within:border-red-600 transition-colors">
                      <h3 className="text-xs uppercase tracking-wider text-neutral-400 font-medium">
                        {method.title}
                      </h3>
                      <p className="text-base font-medium text-neutral-950">
                        {method.contact}
                      </p>
                      <p className="text-sm text-neutral-500 font-light">
                        {method.description}
                      </p>
                      <p className="text-xs text-neutral-400 italic font-mono pt-1">
                        {method.meta}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>


        {/* SECTION 3: SYSTEM FAQs (Pristine White Background) */}
        <section className="bg-white py-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="mb-16">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-2">
                Information Base
              </span>
              <h2 className="text-3xl font-light text-neutral-950 tracking-tight">Frequently Answered Queries</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
              {faqs.map((faq, index) => (
                <div key={index} className="space-y-3">
                  <h3 className="text-base font-medium text-neutral-950 tracking-tight flex gap-2">
                    <span className="text-red-600 font-mono">Q.</span>
                    {faq.q}
                  </h3>
                  <p className="text-sm text-neutral-500 font-light leading-relaxed pl-5">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* SECTION 4: CTA FINAL BLOCK (Deep Charcoal Background) */}
        <section className="bg-neutral-950 text-white py-24">
          <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
            <span className="text-xs uppercase tracking-[0.2em] text-red-500 font-semibold block">
              Ecosystem Launch
            </span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">
              Ready to execute your transaction safely?
            </h2>
            <p className="text-neutral-400 font-light max-w-xl mx-auto text-sm sm:text-base">
              Bypass fraudulent loops entirely. Deploy our specialized structural framework to discover rentals or manage land safely today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <a
                href="/property-search-browse"
                className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-colors duration-200"
              >
                Initiate Rent Hunting
              </a>
              <button className="inline-flex items-center justify-center px-6 py-3 border border-neutral-800 text-neutral-300 text-sm font-medium tracking-tight hover:bg-neutral-900 transition-colors duration-200">
                Deploy Land Asset Listing
              </button>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default ContactUs;